import React, { useState, useRef } from 'react'

const ProjectDisplay = ({
	title,
	desc,
	technologies,
	liveLink,
	githubLink,
	image,
}) => {
	const [isHovered, setIsHovered] = useState(false)
	const [imgError, setImgError] = useState(false)
	const cardRef = useRef(null)
	const sheenRef = useRef(null)
	const hasImage = image && !imgError

	const handleMouseMove = (e) => {
		if (!cardRef.current) return
		const rect = cardRef.current.getBoundingClientRect()
		const x = e.clientX - rect.left
		const y = e.clientY - rect.top

		// 3D tilt
		const rotateY = (x / rect.width - 0.5) * 14
		const rotateX = -(y / rect.height - 0.5) * 14
		cardRef.current.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.03,1.03,1.03)`

		// Holographic sheen — follows cursor with iridescent shimmer
		if (sheenRef.current) {
			const px = (x / rect.width) * 100
			const py = (y / rect.height) * 100
			const angle = Math.atan2(y - rect.height / 2, x - rect.width / 2) * (180 / Math.PI)
			sheenRef.current.style.background = `
				radial-gradient(ellipse at ${px}% ${py}%,
					rgba(255,255,255,0.14) 0%,
					rgba(96,165,250,0.07) 25%,
					rgba(34,211,238,0.04) 50%,
					transparent 65%
				),
				linear-gradient(
					${angle + 90}deg,
					rgba(255,80,80,0.03),
					rgba(80,80,255,0.06),
					rgba(80,255,200,0.03)
				)
			`
			sheenRef.current.style.opacity = '1'
		}
	}

	const handleMouseEnter = () => setIsHovered(true)

	const handleMouseLeave = () => {
		setIsHovered(false)
		if (cardRef.current) {
			cardRef.current.style.transform =
				'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)'
			cardRef.current.style.transition = 'transform 0.5s cubic-bezier(0.23,1,0.32,1)'
		}
		if (sheenRef.current) {
			sheenRef.current.style.opacity = '0'
		}
	}

	return (
		<div
			ref={cardRef}
			className='relative rounded-2xl overflow-hidden cursor-pointer'
			style={{
				height: '300px',
				transition: 'transform 0.12s ease',
				willChange: 'transform',
				transformStyle: 'preserve-3d',
			}}
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}>
			{/* Project image or gradient fallback */}
			{hasImage ? (
				<img
					src={`/images/${image}`}
					alt={title}
					onError={() => setImgError(true)}
					className='w-full h-full object-cover'
					style={{
						transition: 'transform 0.6s ease',
						transform: isHovered ? 'scale(1.08)' : 'scale(1)',
					}}
				/>
			) : (
				<div
					className='w-full h-full flex items-center justify-center overflow-hidden'
					style={{
						background:
							'linear-gradient(135deg, rgba(15,23,42,1) 0%, rgba(30,27,75,1) 50%, rgba(12,74,110,0.8) 100%)',
					}}>
					<div
						className='absolute inset-0 opacity-10'
						style={{
							backgroundImage:
								'linear-gradient(rgba(99,179,237,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,179,237,0.3) 1px, transparent 1px)',
							backgroundSize: '40px 40px',
						}}
					/>
					<div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl' />
					<span className='relative text-white/10 font-bold text-8xl select-none'>
						{title.charAt(0)}
					</span>
				</div>
			)}

			{/* Holographic sheen overlay */}
			<div
				ref={sheenRef}
				className='absolute inset-0 pointer-events-none rounded-2xl'
				style={{
					opacity: 0,
					transition: 'opacity 0.3s ease',
					mixBlendMode: 'screen',
					zIndex: 20,
				}}
			/>

			{/* Bottom gradient + title */}
			<div className='absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex items-end p-5'>
				<h3 className='text-white font-semibold text-sm leading-tight line-clamp-2'>
					{title}
				</h3>
			</div>

			{/* Hover overlay */}
			<div
				className='absolute inset-0 flex flex-col justify-between p-6'
				style={{
					background:
						'linear-gradient(160deg, rgba(15,23,42,0.96) 0%, rgba(3,7,18,0.99) 100%)',
					opacity: isHovered ? 1 : 0,
					transform: isHovered ? 'translateY(0px)' : 'translateY(10px)',
					transition: 'opacity 0.35s ease, transform 0.35s ease',
					zIndex: 10,
				}}>
				{/* Top shimmer line */}
				<div
					className='absolute top-0 left-0 right-0 h-px'
					style={{
						background:
							'linear-gradient(90deg, transparent, rgba(59,130,246,0.7), rgba(6,182,212,0.5), transparent)',
					}}
				/>

				<div>
					<h3 className='text-white font-bold text-base mb-2 leading-tight'>
						{title}
					</h3>
					<p className='text-gray-400 text-xs leading-relaxed line-clamp-3'>
						{desc}
					</p>
				</div>

				<div>
					<div className='flex flex-wrap gap-1.5 mb-4'>
						{technologies?.slice(0, 5).map((tech) => (
							<span
								key={tech}
								className='text-xs px-2 py-0.5 bg-blue-500/15 text-blue-300 border border-blue-500/25 rounded-md'>
								{tech}
							</span>
						))}
						{technologies?.length > 5 && (
							<span className='text-xs px-2 py-0.5 bg-white/5 text-gray-500 rounded-md'>
								+{technologies.length - 5}
							</span>
						)}
					</div>

					<div className='flex gap-2'>
						{liveLink ? (
							<a
								href={liveLink}
								target='_blank'
								rel='noopener noreferrer'
								onClick={(e) => e.stopPropagation()}
								className='flex-1 text-center py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-semibold rounded-lg hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300'>
								View Live
							</a>
						) : (
							<span className='flex-1 text-center py-2 bg-white/5 text-gray-500 text-xs rounded-lg border border-white/5'>
								In Progress
							</span>
						)}
						{githubLink && (
							<a
								href={githubLink}
								target='_blank'
								rel='noopener noreferrer'
								onClick={(e) => e.stopPropagation()}
								className='flex-1 text-center py-2 border border-white/20 text-gray-300 text-xs font-semibold rounded-lg hover:bg-white/5 hover:border-white/40 transition-all duration-300'>
								GitHub
							</a>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ProjectDisplay
